import { getAuthApiErrorText, parseAuthApiError } from "@/utils/authApiError";

/** i18n keys for magic-link toasts (default namespace + `translation:` prefix). */
export const magicLinkErrorKeys = {
  requestFailed: "translation:newWelcome.magicLink.errors.requestFailed",
  rateLimited: "translation:newWelcome.magicLink.errors.rateLimited",
  notAllowed: "translation:newWelcome.magicLink.errors.notAllowed",
  invalidLink: "translation:newWelcome.magicLink.errors.invalidLink",
  expiredLink: "translation:newWelcome.magicLink.errors.expiredLink",
  usedLink: "translation:newWelcome.magicLink.errors.usedLink",
  consumeFailed: "translation:newWelcome.magicLink.errors.consumeFailed",
} as const;

export type MagicLinkRequestError = "rate_limited" | "not_allowed" | "generic";

export type MagicLinkConsumeError =
  "used_link" | "expired_link" | "invalid_link" | "generic";

type MagicLinkErrorKey =
  (typeof magicLinkErrorKeys)[keyof typeof magicLinkErrorKeys];

const requestErrorKey: Record<MagicLinkRequestError, MagicLinkErrorKey> = {
  rate_limited: magicLinkErrorKeys.rateLimited,
  not_allowed: magicLinkErrorKeys.notAllowed,
  generic: magicLinkErrorKeys.requestFailed,
};

const consumeErrorKey: Record<MagicLinkConsumeError, MagicLinkErrorKey> = {
  used_link: magicLinkErrorKeys.usedLink,
  expired_link: magicLinkErrorKeys.expiredLink,
  invalid_link: magicLinkErrorKeys.invalidLink,
  generic: magicLinkErrorKeys.consumeFailed,
};

export const getMagicLinkRequestErrorKey = (
  reason: MagicLinkRequestError,
): MagicLinkErrorKey => requestErrorKey[reason];

export const getMagicLinkConsumeErrorKey = (
  reason: MagicLinkConsumeError,
): MagicLinkErrorKey => consumeErrorKey[reason];

export const classifyMagicLinkRequestError = (
  error: unknown,
): MagicLinkRequestError => {
  const apiError = parseAuthApiError(error);
  const message = getAuthApiErrorText(error);
  const category = apiError?.category?.toUpperCase().replace(/\s/g, "_") ?? "";

  if (
    category === "RATE_LIMIT" ||
    message.includes("maximum number of login attempts")
  ) {
    return "rate_limited";
  }

  if (message.includes("magic login not allowed")) {
    return "not_allowed";
  }

  return "generic";
};

export const isMagicLoginNotAllowedError = (error: unknown): boolean =>
  classifyMagicLinkRequestError(error) === "not_allowed";
